import { type TypedDocumentNode, gql } from '@apollo/client';
import type { CSSProperties } from 'react';
import styled, { css } from 'styled-components';

import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { CircularProgress } from '@sorare/core/src/atoms/loader/CircularProgress';
import {
  BrandS,
  BrandXS,
  LabelM,
  LabelS,
} from '@sorare/core/src/atoms/typography';
import { Ellipsis } from '@sorare/core/src/atoms/typography/Ellipsis';
import { RankingRatio } from '@sorare/core/src/components/collections/RankingRatio';

import type { CollectionPreviewHeader_cardCollection } from './__generated__/index.graphql';

const Root = styled(Horizontal)`
  height: 100%;
  overflow: hidden;
`;
const ClubLogo = styled.img`
  height: 32px;
  object-fit: contain;
`;

const nameStyles = css`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const NameSmall = styled(BrandXS)`
  ${nameStyles}
`;
const NameMedium = styled(BrandS)`
  ${nameStyles}
`;

const Stats = styled(Horizontal)`
  align-items: baseline;
`;

const RightContent = styled(Vertical)`
  overflow: hidden;
`;

const StyledCircularProgress = styled(CircularProgress)`
  width: 12px;
  height: 12px;
`;

const Dot = styled.div`
  width: 3px;
  height: 3px;
  background-color: var(--c-nd-150);
  border-radius: 50%;
  align-self: center;
`;

type Props = {
  cardCollection: Nullable<CollectionPreviewHeader_cardCollection>;
  small?: boolean;
  leagueColor: string;
};

export const CollectionPreviewHeader = ({
  cardCollection,
  small,
  leagueColor,
}: Props) => {
  const { anyTeam, userCardCollection } = cardCollection || {};
  const { liveRankingRatio, liveRanking } = userCardCollection || {};
  const { pictureUrl, shortName } = anyTeam || {};

  const fulfilledSlotsCount = userCardCollection?.fulfilledSlotsCount ?? 0;
  const slotsCount = userCardCollection?.slotsCount ?? 1;
  const Name = small ? NameSmall : NameMedium;
  return (
    <Root>
      {!!pictureUrl && <ClubLogo src={pictureUrl} alt="" />}
      <RightContent gap={0.5}>
        <Name>{shortName}</Name>
        <Stats>
          <Horizontal gap={0.5} style={{ alignItems: 'baseline' }}>
            <LabelM bold>{fulfilledSlotsCount}</LabelM>{' '}
            <Ellipsis>
              <LabelS>/ {slotsCount}</LabelS>
            </Ellipsis>
          </Horizontal>
          <StyledCircularProgress
            progress={fulfilledSlotsCount}
            maxProgress={slotsCount}
            style={
              {
                '--circular-progress-stroke': leagueColor,
              } as CSSProperties
            }
          />
          <Dot />
          <Ellipsis>
            <LabelS color="var(--c-nd-600)">
              <RankingRatio
                liveRankingRatio={liveRankingRatio || 0}
                liveRanking={liveRanking || 0}
              />
            </LabelS>
          </Ellipsis>
        </Stats>
      </RightContent>
    </Root>
  );
};

CollectionPreviewHeader.fragments = {
  cardCollection: gql`
    fragment CollectionPreviewHeader_cardCollection on CardCollection {
      slug
      anyTeam {
        slug
        shortName
        pictureUrl(derivative: "avatar")
      }
      userCardCollection(forUserSlug: $userSlug) {
        id
        slotsCount
        fulfilledSlotsCount
        liveRankingRatio
        liveRanking
      }
    }
  ` as TypedDocumentNode<CollectionPreviewHeader_cardCollection>,
};
